import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
    FileText,
    Wrench,
    Users,
    Package,
    MessageSquare,
    Mail,
    ShieldCheck,
    Truck,
    Box,
    ChevronLeft,
    ChevronRight,
    Beaker,
    Layers,
} from 'lucide-react';

// Components
import Admin from './asset-management';
import EquipmentSubCategory from './EquipmentSubCategory';
import DocControlMangement from '../../components/Calibration/DocControlMangement';
import AdminEmployees from '../../components/AdminEmployees';
import Spares from './spares-managment';
import ToolboxAdmin from '../../components/ToolboxAdmin';
import AdminReportingEmailPage from '../../components/AdminReportingEmailPage';
import UserManagementAGGrid from '../userManagement/UserManagementAGGrid';
import ShippingManagement from '../../components/ShippingManagement/ShippingManagement';
import { ConsumableMasterAdmin, MaterialMasterAdmin } from '../../components/admin/ConsumableMaterialAdmin';

// Styles
import '../technical/HeatProfilePage.css';
import '../calibration/CalibrationPage.css';
import './AdminModernPage.css';
import '../../layout/ProjectTabsStrip.css';

const TAB_ITEMS = [
    { id: 'asset-management', label: 'Asset Management', icon: <Box size={18} />, path: '/admin/asset-management' },
    { id: 'documentation', label: 'Documentation', icon: <FileText size={18} />, path: '/admin/documentation' },
    { id: 'calibration', label: 'Calibration & Control', icon: <Wrench size={18} />, path: '/admin/calibration-control-users' },
    { id: 'employees', label: 'Employees', icon: <Users size={18} />, path: '/admin/employees' },
    { id: 'spares', label: 'Spares', icon: <Package size={18} />, path: '/admin/spares' },
    { id: 'consumable', label: 'Consumable', icon: <Beaker size={18} />, path: '/admin/consumable' },
    { id: 'material', label: 'Material', icon: <Layers size={18} />, path: '/admin/material' },
    { id: 'communication', label: 'Communication', icon: <MessageSquare size={18} />, path: '/admin/communications' },
    { id: 'reporting-email', label: 'Reporting Email', icon: <Mail size={18} />, path: '/admin/reporting-email' },
    { id: 'user-management', label: 'User Management', icon: <ShieldCheck size={18} />, path: '/admin/user-management' },
    { id: 'shipping-management', label: 'Shipping Management', icon: <Truck size={18} />, path: '/admin/shipping-management' },
];

function getTabIdForPathname(pathname) {
    const item = TAB_ITEMS.find((t) => t.path === pathname);
    return item ? item.id : 'documentation';
}

const Tabs = ({ items, activeTabId, onTabClick }) => {
    const tabsViewportRef = useRef(null);
    const [tabScrollState, setTabScrollState] = useState({
        hasOverflow: false,
        canPrev: false,
        canNext: false,
    });

    const updateTabScrollEdges = useCallback(() => {
        const el = tabsViewportRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        const hasOverflow = scrollWidth > clientWidth + 2;
        setTabScrollState({
            hasOverflow,
            canPrev: hasOverflow && scrollLeft > 2,
            canNext: hasOverflow && scrollLeft < scrollWidth - clientWidth - 2,
        });
    }, []);

    useEffect(() => {
        if (!tabsViewportRef.current || !activeTabId) return;
        const activeEl = tabsViewportRef.current.querySelector('.calibration-tab-item.active');
        if (activeEl) {
            activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
    }, [activeTabId]);

    useEffect(() => {
        const id = requestAnimationFrame(() => {
            updateTabScrollEdges();
        });
        return () => cancelAnimationFrame(id);
    }, [items, updateTabScrollEdges]);

    useEffect(() => {
        const el = tabsViewportRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(() => {
            updateTabScrollEdges();
        });
        ro.observe(el);
        return () => {
            ro.disconnect();
        };
    }, [updateTabScrollEdges, items.length]);

    const scrollTabsBy = (direction) => {
        const el = tabsViewportRef.current;
        if (!el) return;
        const delta = Math.min(280, Math.max(120, Math.floor(el.clientWidth * 0.75)));
        el.scrollBy({ left: direction * delta, behavior: 'smooth' });
    };

    const { hasOverflow, canPrev, canNext } = tabScrollState;

    return (
        <div
            className={`calibration-tabs-container modern-admin-tabs project-tabs-strip${!hasOverflow ? ' project-tabs-strip--no-overflow' : ''}`}
        >
            <div className="project-tabs-strip__row">
                <button
                    type="button"
                    className="project-tabs-strip__btn project-tabs-strip__btn--prev"
                    onClick={() => scrollTabsBy(-1)}
                    disabled={!hasOverflow || !canPrev}
                    aria-label="Scroll admin tabs left"
                >
                    <ChevronLeft size={22} strokeWidth={2} aria-hidden />
                </button>
                <div
                    className="project-tabs-strip__viewport"
                    ref={tabsViewportRef}
                    onScroll={updateTabScrollEdges}
                    role="presentation"
                >
                    <div className="calibration-tabs">
                        {items.map((item) => (
                            <div
                                key={item.id}
                                className={`calibration-tab-item ${activeTabId === item.id ? 'active' : ''}`}
                                onClick={() => onTabClick(item)}
                            >
                                {item.icon}
                                {item.label}
                            </div>
                        ))}
                    </div>
                </div>
                <button
                    type="button"
                    className="project-tabs-strip__btn project-tabs-strip__btn--next"
                    onClick={() => scrollTabsBy(1)}
                    disabled={!hasOverflow || !canNext}
                    aria-label="Scroll admin tabs right"
                >
                    <ChevronRight size={22} strokeWidth={2} aria-hidden />
                </button>
            </div>
        </div>
    );
};

const AdminModernPage = () => {
    const history = useHistory();
    const location = useLocation();
    // Must match the URL on first paint; a wrong default (e.g. "documentation") mounts the wrong
    // tab briefly and duplicates all admin XHRs (product-hierarchies, pih-equipment-types, etc.).
    const [activeTab, setActiveTab] = useState(() => getTabIdForPathname(location.pathname));

    // Sync active tab with URL (back/forward, external navigation)
    useEffect(() => {
        setActiveTab(getTabIdForPathname(location.pathname));
    }, [location.pathname]);

    const handleBackClick = () => {
        history.push('/admin');
    };

    const handleTabClick = (item) => {
        setActiveTab(item.id);
        history.push(item.path);
    };

    return (
        <div className="heat-profile-page admin-modern-page">
            <Tabs
                items={TAB_ITEMS}
                activeTabId={activeTab}
                onTabClick={handleTabClick}
            />

            <div className="modern-admin-content">
                {activeTab === 'asset-management' && <Admin isModernPage={true} />}
                {activeTab === 'documentation' && <EquipmentSubCategory />}
                {activeTab === 'calibration' && <DocControlMangement className="mt-0" />}
                {activeTab === 'employees' && <AdminEmployees />}
                {activeTab === 'spares' && <Spares />}
                {activeTab === 'communication' && <ToolboxAdmin />}
                {activeTab === 'reporting-email' && <AdminReportingEmailPage />}
                {activeTab === 'consumable' && <ConsumableMasterAdmin />}
                {activeTab === 'material' && <MaterialMasterAdmin />}
                {activeTab === 'user-management' && <UserManagementAGGrid />}
                {activeTab === 'shipping-management' && <ShippingManagement isModernPage={true} />}
            </div>
        </div>
    );
};

export default AdminModernPage;
